import React from 'react';
import {
    Modal,
    Text,
    ActionIcon,
    Divider,
    Box,
    Paper,
} from '@mantine/core';
import { Button } from '@xplatform/design-system';
import { IconX, IconCircleCheck, IconCircleX, IconCircleFilled } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import "./InstructionsHelpDialog.scss";

interface InstructionsHelpDialogProps {
    open: boolean;
    onClose: () => void;
}

const InstructionsHelpDialog: React.FC<InstructionsHelpDialogProps> = ({ open, onClose }) => {
    const { t } = useTranslation();

    return (
        <Modal
            opened={open}
            onClose={onClose}
            size="lg"
            withCloseButton={false}
            className="instructions-help-dialog"
            portalProps={{ target: '#extraction-portal-root' }}
        >
            <Box className="dialog-header">
                <Text size="lg" fw={600} component="span">
                    {t('extraction.instructionsHelp.title')}
                </Text>
                <ActionIcon
                    onClick={onClose}
                    aria-label="close"
                    size="sm"
                    className="icon-primary"
                    variant="subtle"
                >
                    <IconX className="icon-primary" size={16} />
                </ActionIcon>
            </Box>

            <Divider />

            <Box className="dialog-content">
                <Text size="md" className="intro-text">
                    {t('extraction.instructionsHelp.intro')}
                </Text>

                <div className="section">
                    <Text size="lg" fw={600} className="section-title">
                        {t('extraction.instructionsHelp.sections.specific.title')}
                    </Text>
                    <Text size="sm" className="section-description">
                        {t('extraction.instructionsHelp.sections.specific.description')}
                    </Text>
                    <Box className="examples-box">
                        <div className="example-item good">
                            <IconCircleCheck className="icon" size={18} />
                            <Text size="sm">
                                {t('extraction.instructionsHelp.sections.specific.good')}
                            </Text>
                        </div>
                        <div className="example-item bad">
                            <IconCircleX className="icon" size={18} />
                            <Text size="sm">
                                {t('extraction.instructionsHelp.sections.specific.bad')}
                            </Text>
                        </div>
                    </Box>
                </div>

                <div className="section">
                    <Text size="lg" fw={600} className="section-title">
                        {t('extraction.instructionsHelp.sections.format.title')}
                    </Text>
                    <Text size="sm" className="section-description">
                        {t('extraction.instructionsHelp.sections.format.description')}
                    </Text>
                    <Box className="bullet-list">
                        <div className="bullet-item">
                            <IconCircleFilled className="bullet-icon" size={6} />
                            <Text size="sm">
                                {t('extraction.instructionsHelp.sections.format.list')}
                            </Text>
                        </div>
                        <div className="bullet-item">
                            <IconCircleFilled className="bullet-icon" size={6} />
                            <Text size="sm">
                                {t('extraction.instructionsHelp.sections.format.table')}
                            </Text>
                        </div>
                        <div className="bullet-item">
                            <IconCircleFilled className="bullet-icon" size={6} />
                            <Text size="sm">
                                {t('extraction.instructionsHelp.sections.format.json')}
                            </Text>
                        </div>
                    </Box>
                </div>

                <div className="section">
                    <Text size="lg" fw={600} className="section-title">
                        {t('extraction.instructionsHelp.sections.rules.title')}
                    </Text>
                    <Text size="sm" className="section-description">
                        {t('extraction.instructionsHelp.sections.rules.description')}
                    </Text>
                    <Box className="bullet-list">
                        <div className="bullet-item">
                            <IconCircleFilled className="bullet-icon" size={6} />
                            <Text size="sm">
                                {t('extraction.instructionsHelp.sections.rules.example1')}
                            </Text>
                        </div>
                        <div className="bullet-item">
                            <IconCircleFilled className="bullet-icon" size={6} />
                            <Text size="sm">
                                {t('extraction.instructionsHelp.sections.rules.example2')}
                            </Text>
                        </div>
                    </Box>
                </div>

                <Divider className="section-divider" />

                <div className="section">
                    <Text size="lg" fw={600} className="section-title-main">
                        {t('extraction.instructionsHelp.sections.summary.title')}
                    </Text>

                    <div className="code-examples">
                        <div className="code-example">
                            <Text size="sm" fw={600} className="example-title">
                                {t('extraction.instructionsHelp.sections.summary.simple.title')}
                            </Text>
                            <Paper className="code-block">
                                <Text size="sm">
                                    {t('extraction.instructionsHelp.sections.summary.simple.text')}
                                </Text>
                            </Paper>
                        </div>

                        <div className="code-example">
                            <Text size="sm" fw={600} className="example-title">
                                {t('extraction.instructionsHelp.sections.summary.detailed.title')}
                            </Text>
                            <Paper className="code-block">
                                <Text size="sm">
                                    {t('extraction.instructionsHelp.sections.summary.detailed.text')}
                                </Text>
                            </Paper>
                        </div>

                        <div className="code-example">
                            <Text size="sm" fw={600} className="example-title">
                                {t('extraction.instructionsHelp.sections.summary.advanced.title')}
                            </Text>
                            <Paper className="code-block">
                                <Text size="sm">
                                    {t('extraction.instructionsHelp.sections.summary.advanced.text')}
                                </Text>
                            </Paper>
                        </div>
                    </div>
                </div>
            </Box>

            <Box className="dialog-footer">
                <Button onClick={onClose}>
                    {t('extraction.instructionsHelp.button')}
                </Button>
            </Box>
        </Modal>
    );
};

export default InstructionsHelpDialog;
